import { useState } from "react";
import { Box, Chip, CircularProgress, Grid2 as Grid, Switch, TextField, Typography } from "@mui/material";
import { useNotify, useRefresh } from "react-admin";
import { ResourceName, AlertType, type TaskTemplate } from "@/types";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { ColorType } from "@/types/shared/uiComponents";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";

interface TemplateInfoSectionProps {
  template: TaskTemplate;
}

/**
 * Human-readable descriptions for trigger events.
 */
const TRIGGER_DESCRIPTIONS: Record<string, string> = {
  threshold_50_percent: "Fires when YTD spend crosses 50% of spec deductible.",
  threshold_85_percent: "Fires when YTD spend crosses 85% of spec deductible.",
  threshold_100_percent: "Fires when YTD spend reaches or exceeds spec deductible.",
  icd_trigger_detected: "Fires when a claim contains ICD codes matching the trigger list.",
  stoploss_claim_update: "Fires when new spend is recorded for a claimant with an active claim.",
  prompt_pay_required: "Fires when a cash/both funded claimant has an active claim with new spend.",
  placeholder_client_created: "Fires when a placeholder client is created.",
  placeholder_policy_created: "Fires when a placeholder policy is created.",
};

/**
 * Compact field row — label on left, value on right.
 */
const FieldRow = ({
  label,
  helperText,
  children,
}: {
  label: string;
  helperText?: string;
  children: React.ReactNode;
}) => (
  <Grid container spacing={1} sx={{ py: 0.75, alignItems: "flex-start" }}>
    <Grid size={{ xs: 5 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      {helperText && (
        <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: "block" }}>
          {helperText}
        </Typography>
      )}
    </Grid>
    <Grid size={{ xs: 7 }}>{children}</Grid>
  </Grid>
);

/**
 * Thin visual divider label for sub-groups within a section.
 */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 2, mb: 0.5, letterSpacing: 1.2 }}>
    {children}
  </Typography>
);

export const TemplateInfoSection = ({ template }: TemplateInfoSectionProps) => {
  const { canEdit } = useResourcePermissions(ResourceName.TASK_TEMPLATES);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [isUpdating, setIsUpdating] = useState(false);

  const patchTemplate = async (updates: Partial<TaskTemplate>) => {
    setIsUpdating(true);
    const apiUrl = getPartnerAwareApiUrl();
    try {
      await httpClient(`${apiUrl}/${ResourceName.TASK_TEMPLATES}/${template.id}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      notify("Template updated", { type: AlertType.SUCCESS });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBlurUpdate = (field: string) => (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const currentValue = template[field as keyof TaskTemplate];
    if (value === String(currentValue ?? "")) return;

    const numericFields = ["priority", "default_due_days", "rough_order"];
    if (numericFields.includes(field)) {
      const numVal = value === "" ? undefined : parseInt(value, 10);
      if (value !== "" && isNaN(numVal as number)) return;
      void patchTemplate({ [field]: numVal });
    } else {
      void patchTemplate({ [field]: value || undefined });
    }
  };

  const objectTypes = Array.isArray(template.applicable_object_types)
    ? template.applicable_object_types
    : template.applicable_object_types
      ? [template.applicable_object_types]
      : [];

  const triggerDesc = template.trigger_event ? TRIGGER_DESCRIPTIONS[template.trigger_event] : undefined;

  return (
    <Box>
      {isUpdating && (
        <Box display="flex" justifyContent="center" py={0.5}>
          <CircularProgress size={18} />
        </Box>
      )}

      {/* ── Identity ── */}
      <FieldRow label="Title">
        {canEdit ? (
          <TextField
            size="small"
            fullWidth
            defaultValue={template.title}
            onBlur={handleBlurUpdate("title")}
            variant="standard"
          />
        ) : (
          <Typography variant="body2">{template.title}</Typography>
        )}
      </FieldRow>

      <FieldRow label="Description">
        {canEdit ? (
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={3}
            defaultValue={template.description ?? ""}
            onBlur={handleBlurUpdate("description")}
            variant="standard"
            placeholder="No description"
          />
        ) : (
          <Typography variant="body2">{template.description || "—"}</Typography>
        )}
      </FieldRow>

      <FieldRow label="Status">
        <StatusChip
          label={template.is_active ? "Active" : "Inactive"}
          color={template.is_active ? ColorType.Success : ColorType.Default}
        />
      </FieldRow>

      {/* ── Trigger ── */}
      <SectionLabel>Trigger</SectionLabel>

      <FieldRow label="Trigger Event">
        <Typography variant="body2" fontWeight={500}>
          {template.trigger_event ? formatToTitleCase(template.trigger_event) : "—"}
        </Typography>
        {triggerDesc && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {triggerDesc}
          </Typography>
        )}
      </FieldRow>

      <FieldRow label="Record Subject">
        <Typography variant="body2">
          {template.record_subject ? formatToTitleCase(template.record_subject) : "—"}
        </Typography>
      </FieldRow>

      <FieldRow label="Category">
        {template.template_category ? (
          <StatusChip label={formatToTitleCase(template.template_category)} color={ColorType.Info} />
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </FieldRow>

      {objectTypes.length > 0 && (
        <FieldRow label="Object Types">
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {objectTypes.map((type) => (
              <Chip key={type} label={formatToTitleCase(type)} size="small" />
            ))}
          </Box>
        </FieldRow>
      )}

      {template.event_status_filters && template.event_status_filters.length > 0 && (
        <FieldRow label="Status Filters">
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {template.event_status_filters.map((filter) => (
              <Chip key={filter} label={formatToTitleCase(filter)} size="small" variant="outlined" />
            ))}
          </Box>
        </FieldRow>
      )}

      {/* ── Task Defaults ── */}
      <SectionLabel>Task Defaults</SectionLabel>

      <FieldRow label="Priority" helperText="1 = highest">
        {canEdit ? (
          <TextField
            size="small"
            type="number"
            defaultValue={template.priority ?? ""}
            onBlur={handleBlurUpdate("priority")}
            variant="standard"
            sx={{ width: 80 }}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        ) : (
          <Typography variant="body2">{template.priority ?? "—"}</Typography>
        )}
      </FieldRow>

      <FieldRow label="Due Days" helperText="Days until task is due">
        {canEdit ? (
          <TextField
            size="small"
            type="number"
            defaultValue={template.default_due_days ?? ""}
            onBlur={handleBlurUpdate("default_due_days")}
            variant="standard"
            sx={{ width: 80 }}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        ) : (
          <Typography variant="body2">{template.default_due_days ?? "—"}</Typography>
        )}
      </FieldRow>

      <FieldRow label="Show on Exhibit" helperText="Include on claim exhibit">
        <Switch
          checked={!!template.default_show_on_claim_exhibit}
          disabled={!canEdit}
          size="small"
          onChange={(_, checked) => {
            void patchTemplate({ default_show_on_claim_exhibit: checked });
          }}
        />
      </FieldRow>

      <FieldRow label="Display Order" helperText="Lower = first">
        {canEdit ? (
          <TextField
            size="small"
            type="number"
            defaultValue={template.rough_order ?? ""}
            onBlur={handleBlurUpdate("rough_order")}
            variant="standard"
            sx={{ width: 80 }}
          />
        ) : (
          <Typography variant="body2">{template.rough_order ?? "—"}</Typography>
        )}
      </FieldRow>

      {/* ── Audit ── */}
      {(template.created_datetime || template.modified_datetime) && (
        <>
          <SectionLabel>Audit</SectionLabel>
          {template.created_datetime && (
            <FieldRow label="Created">
              <Typography variant="body2">
                {new Date(template.created_datetime).toLocaleDateString()}
                {template.created_by && ` by ${template.created_by}`}
              </Typography>
            </FieldRow>
          )}
          {template.modified_datetime && (
            <FieldRow label="Last Modified">
              <Typography variant="body2">
                {new Date(template.modified_datetime).toLocaleDateString()}
                {template.modified_by && ` by ${template.modified_by}`}
              </Typography>
            </FieldRow>
          )}
        </>
      )}
    </Box>
  );
};
